import {
  SOFTPHONE_CHOSEN_PRESENCE,
  SOFTPHONE_PRESENCE_LABELS,
  type SoftphoneCallState,
  type SoftphoneChosenPresence,
  type SoftphonePresence,
} from '@lia/contracts';
import {
  ArrowDataTransferHorizontalIcon,
  Call02Icon,
  CallEnd01Icon,
  CallIncoming01Icon,
  CallOutgoing01Icon,
  Cancel01Icon,
  Delete02Icon,
  DialpadSquare01Icon,
  Mic01Icon,
  MicOff01Icon,
  PauseIcon,
  PlayIcon,
  TelephoneIcon,
} from '@hugeicons/core-free-icons';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatPhone } from '../../lib/format.js';
import { StatusBadge, type StatusTone } from '../primitives/badge.js';
import { Button, IconButton } from '../primitives/button.js';
import { RadioGroup } from '../primitives/checkbox.js';
import { Dialog } from '../primitives/dialog.js';
import { FormField } from '../primitives/form-field.js';
import { Icon } from '../primitives/icon.js';
import { Input } from '../primitives/input.js';
import { InlineNotice } from '../primitives/notice.js';
import { Select } from '../primitives/select.js';
import { Spinner } from '../primitives/spinner.js';

// Advisor softphone presentational components (plan B3). Everything arrives by props: these components never place,
// answer or bridge a call themselves — the app wires them to LIA's API and the WebRTC SDK. A call is shown as
// connected only when the caller passes `state="connected"` (the server confirmed the bridge).

// ───────────── Labels / tones ─────────────

export const SOFTPHONE_PRESENCE_TONE: Record<SoftphonePresence, StatusTone> = {
  available: 'success',
  busy: 'accent',
  away: 'warning',
  offline: 'neutral',
};

export const SOFTPHONE_CALL_STATE_LABELS: Record<SoftphoneCallState, string> = {
  dialing: 'Conectando tu softphone…',
  calling: 'Llamando…',
  ringing: 'Llamada entrante',
  connected: 'En llamada',
  ended: 'Llamada terminada',
};

export const SOFTPHONE_CALL_STATE_TONE: Record<SoftphoneCallState, StatusTone> = {
  dialing: 'info',
  calling: 'info',
  ringing: 'warning',
  connected: 'success',
  ended: 'neutral',
};

const TRANSFER_STATE_LABELS = { ringing: 'Transfiriendo…', answered: 'Transferida', failed: 'Transferencia fallida' } as const;
const TRANSFER_STATE_TONE: Record<keyof typeof TRANSFER_STATE_LABELS, StatusTone> = { ringing: 'info', answered: 'success', failed: 'danger' };

// ───────────── Timer ─────────────

/** Elapsed time between an ISO start and `now` as m:ss (h:mm:ss after an hour). Unknown / future start → "0:00". */
export function formatCallTimer(startIso: string | null | undefined, now: number = Date.now()): string {
  const start = startIso ? Date.parse(startIso) : NaN;
  const total = Number.isFinite(start) ? Math.max(0, Math.floor((now - start) / 1000)) : 0;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

export interface CallTimerProps {
  /** ISO instant the timer counts from (e.g. when the server says the call was answered). */
  startedAt: string;
  /** Stop the clock at this instant (ended calls). */
  endedAt?: string | null;
  className?: string;
}

/** Live call duration (ticks every second). Not announced by screen readers on every tick. */
export function CallTimer({ startedAt, endedAt, className }: CallTimerProps) {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (endedAt) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [endedAt]);
  const end = endedAt ? Date.parse(endedAt) : now;
  const text = formatCallTimer(startedAt, Number.isFinite(end) ? end : now);
  return (
    <time role="timer" aria-live="off" aria-label={`Duración ${text}`} dateTime={startedAt} className={cn('ef-tabular text-body font-medium text-text-primary', className)}>
      {text}
    </time>
  );
}

// ───────────── Presence ─────────────

export function PresenceBadge({ presence, className }: { presence: SoftphonePresence; className?: string }) {
  return (
    <StatusBadge tone={SOFTPHONE_PRESENCE_TONE[presence]} className={className}>
      {SOFTPHONE_PRESENCE_LABELS[presence]}
    </StatusBadge>
  );
}

export interface PresencePickerProps {
  /** What the advisor chose (available / away). */
  value: SoftphoneChosenPresence;
  onValueChange: (value: SoftphoneChosenPresence) => void;
  disabled?: boolean;
  className?: string;
}

/** Lets the advisor choose Disponible / Ausente. «En llamada» and «Desconectado» are computed, never chosen. */
export function PresencePicker({ value, onValueChange, disabled, className }: PresencePickerProps) {
  return (
    <Select
      aria-label="Mi disponibilidad"
      value={value}
      onValueChange={(v) => onValueChange(v as SoftphoneChosenPresence)}
      options={SOFTPHONE_CHOSEN_PRESENCE.map((p) => ({ value: p, label: SOFTPHONE_PRESENCE_LABELS[p] }))}
      disabled={disabled}
      className={className}
    />
  );
}

// ───────────── Dial pad ─────────────

const KEYS: readonly { key: string; sub?: string }[] = [
  { key: '1' },
  { key: '2', sub: 'ABC' },
  { key: '3', sub: 'DEF' },
  { key: '4', sub: 'GHI' },
  { key: '5', sub: 'JKL' },
  { key: '6', sub: 'MNO' },
  { key: '7', sub: 'PQRS' },
  { key: '8', sub: 'TUV' },
  { key: '9', sub: 'WXYZ' },
  { key: '*' },
  { key: '0', sub: '+' },
  { key: '#' },
];

const KEY_NAMES: Record<string, string> = { '*': 'asterisco', '#': 'gato' };

export interface DialpadProps {
  /**
   * `dial` (default): number field + keys + call button. `dtmf`: keys only, each press calls `onDigit` (tones during a
   * call, e.g. to navigate a menu).
   */
  mode?: 'dial' | 'dtmf';
  value?: string;
  onValueChange?: (value: string) => void;
  onCall?: (number: string) => void;
  onDigit?: (digit: string) => void;
  callLabel?: string;
  /** Disables the keys and the call button. */
  disabled?: boolean;
  /** Why calling is not possible right now (shown under the button). */
  callDisabledReason?: string | null;
  calling?: boolean;
  className?: string;
}

/** Telephone keypad. In dial mode it edits a number and reports the call; in DTMF mode it reports each key press. */
export function Dialpad({ mode = 'dial', value = '', onValueChange, onCall, onDigit, callLabel = 'Llamar', disabled, callDisabledReason, calling, className }: DialpadProps) {
  const reasonId = React.useId();
  const press = (k: string) => {
    if (disabled) return;
    onDigit?.(k);
    if (mode === 'dial') onValueChange?.(value + k);
  };
  const trimmed = value.trim();
  const canCall = !disabled && !callDisabledReason && trimmed.replace(/[^0-9]/g, '').length >= 3;
  return (
    <div className={cn('grid gap-3', className)}>
      {mode === 'dial' ? (
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (canCall) onCall?.(trimmed);
          }}
        >
          <Input
            aria-label="Número a marcar"
            inputMode="tel"
            autoComplete="off"
            value={value}
            disabled={disabled}
            maxLength={32}
            placeholder="Número o +52…"
            onChange={(e) => onValueChange?.(e.target.value.replace(/[^0-9+*#()\s-]/g, ''))}
            className="ef-tabular text-body-lg"
          />
          <IconButton label="Borrar un dígito" icon={<Icon icon={Delete02Icon} aria-hidden />} disabled={disabled || value.length === 0} onClick={() => onValueChange?.(value.slice(0, -1))} />
        </form>
      ) : null}
      <div role="group" aria-label={mode === 'dtmf' ? 'Teclado de tonos' : 'Teclado'} className="grid grid-cols-3 gap-2">
        {KEYS.map(({ key, sub }) => (
          <button
            key={key}
            type="button"
            disabled={disabled}
            aria-label={KEY_NAMES[key] ?? key}
            onClick={() => press(key)}
            className={cn(
              'grid h-12 place-items-center rounded-control border border-border-subtle bg-surface-default text-text-primary',
              'hover:bg-surface-subtle active:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-focus disabled:opacity-50',
            )}
          >
            <span className="grid leading-none">
              <span className="ef-tabular text-body-lg font-semibold">{key}</span>
              {sub ? <span className="text-meta text-text-secondary">{sub}</span> : null}
            </span>
          </button>
        ))}
      </div>
      {mode === 'dial' ? (
        <div className="grid gap-1">
          <Button
            leadingIcon={<Icon icon={Call02Icon} aria-hidden />}
            disabled={!canCall}
            loading={calling}
            aria-describedby={callDisabledReason ? reasonId : undefined}
            onClick={() => onCall?.(trimmed)}
          >
            {callLabel}
          </Button>
          {callDisabledReason ? (
            <p id={reasonId} className="text-meta text-text-secondary">
              {callDisabledReason}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ───────────── Incoming call ─────────────

export interface IncomingCallCardProps {
  callerName?: string | null;
  callerNumber?: string | null;
  lineName?: string | null;
  onAnswer: () => void;
  onReject: () => void;
  answering?: boolean;
  className?: string;
}

/** Ringing inbound call offered to this advisor. */
export function IncomingCallCard({ callerName, callerNumber, lineName, onAnswer, onReject, answering, className }: IncomingCallCardProps) {
  const who = callerName ?? (callerNumber ? formatPhone(callerNumber) : 'Número desconocido');
  return (
    <section aria-label="Llamada entrante" className={cn('grid gap-3 rounded-card border border-border-subtle bg-surface-subtle p-3', className)}>
      <div className="flex items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-pill bg-warning-bg text-warning">
          <Icon icon={CallIncoming01Icon} aria-hidden className="size-[18px]" />
        </span>
        <div className="grid min-w-0 gap-0.5" role="alert">
          <p className="text-meta font-medium text-text-secondary">Llamada entrante</p>
          <p className="truncate text-body-lg font-semibold text-text-primary">{who}</p>
          {callerName && callerNumber ? <p className="ef-tabular text-meta text-text-secondary">{formatPhone(callerNumber)}</p> : null}
          {lineName ? <p className="text-meta text-text-secondary">Línea: {lineName}</p> : null}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="danger" leadingIcon={<Icon icon={CallEnd01Icon} aria-hidden />} onClick={onReject} disabled={answering}>
          Rechazar
        </Button>
        <Button leadingIcon={<Icon icon={Call02Icon} aria-hidden />} onClick={onAnswer} loading={answering}>
          Contestar
        </Button>
      </div>
    </section>
  );
}

// ───────────── Current call ─────────────

export interface SoftphoneTransferView {
  state: 'ringing' | 'answered' | 'failed';
  targetLabel: string;
  detail: string | null;
}

export interface SoftphoneCallPanelProps {
  state: SoftphoneCallState;
  direction: 'inbound' | 'outbound';
  contactName?: string | null;
  number?: string | null;
  lineName?: string | null;
  /** When the server says the call was answered (timer start). */
  answeredAt?: string | null;
  endedAt?: string | null;
  endReason?: string | null;
  muted: boolean;
  held: boolean;
  onToggleMute: () => void;
  onToggleHold: () => void;
  onTransfer?: () => void;
  onHangup: () => void;
  /** DTMF tones during the call; enables the «Teclado» toggle. */
  onDigit?: (digit: string) => void;
  transfer?: SoftphoneTransferView | null;
  /** Slot for the copilot link / reason it is missing. */
  copilot?: React.ReactNode;
  hangingUp?: boolean;
  className?: string;
}

/** The advisor's current softphone call: state, who, timer and controls (mute, hold, keypad, transfer, hang up). */
export function SoftphoneCallPanel(p: SoftphoneCallPanelProps) {
  const [keypad, setKeypad] = React.useState(false);
  const ended = p.state === 'ended';
  const connected = p.state === 'connected';
  const who = p.contactName ?? (p.number ? formatPhone(p.number) : 'Número desconocido');
  return (
    <section aria-label="Llamada actual" className={cn('grid gap-3', p.className)}>
      <div className="flex items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-pill bg-surface-subtle text-text-secondary">
          <Icon icon={p.direction === 'inbound' ? CallIncoming01Icon : CallOutgoing01Icon} aria-hidden className="size-[18px]" />
        </span>
        <div className="grid min-w-0 flex-1 gap-0.5">
          <p className="truncate text-body-lg font-semibold text-text-primary">{who}</p>
          {p.contactName && p.number ? <p className="ef-tabular text-meta text-text-secondary">{formatPhone(p.number)}</p> : null}
          {p.lineName ? <p className="text-meta text-text-secondary">{p.direction === 'inbound' ? 'Entró por' : 'Desde'}: {p.lineName}</p> : null}
        </div>
        {(connected || ended) && p.answeredAt ? <CallTimer startedAt={p.answeredAt} endedAt={p.endedAt} /> : null}
      </div>
      <div className="flex flex-wrap items-center gap-1.5" role="status" aria-live="polite">
        <StatusBadge tone={SOFTPHONE_CALL_STATE_TONE[p.state]} icon={p.state === 'dialing' || p.state === 'calling' ? <Spinner size="sm" /> : undefined}>
          {SOFTPHONE_CALL_STATE_LABELS[p.state]}
        </StatusBadge>
        {!ended && p.muted ? <StatusBadge tone="warning" icon={<Icon icon={MicOff01Icon} aria-hidden />}>Micrófono silenciado</StatusBadge> : null}
        {!ended && p.held ? <StatusBadge tone="warning" icon={<Icon icon={PauseIcon} aria-hidden />}>En espera</StatusBadge> : null}
        {p.transfer ? (
          <StatusBadge tone={TRANSFER_STATE_TONE[p.transfer.state]}>
            {TRANSFER_STATE_LABELS[p.transfer.state]}: {p.transfer.targetLabel}
          </StatusBadge>
        ) : null}
      </div>
      {p.transfer?.detail ? <p className="text-meta text-text-secondary">{p.transfer.detail}</p> : null}
      {ended && p.endReason ? <p className="text-meta text-text-secondary">{p.endReason}</p> : null}
      {p.copilot ? <div className="grid gap-1">{p.copilot}</div> : null}
      {!ended ? (
        <>
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Controles de la llamada">
            <Button
              size="sm"
              variant="secondary"
              aria-pressed={p.muted}
              leadingIcon={<Icon icon={p.muted ? MicOff01Icon : Mic01Icon} aria-hidden />}
              onClick={p.onToggleMute}
            >
              {p.muted ? 'Activar micrófono' : 'Silenciar'}
            </Button>
            <Button size="sm" variant="secondary" aria-pressed={p.held} disabled={!connected} leadingIcon={<Icon icon={p.held ? PlayIcon : PauseIcon} aria-hidden />} onClick={p.onToggleHold}>
              {p.held ? 'Reanudar' : 'En espera'}
            </Button>
            {p.onDigit ? (
              <Button size="sm" variant="secondary" aria-pressed={keypad} disabled={!connected} leadingIcon={<Icon icon={DialpadSquare01Icon} aria-hidden />} onClick={() => setKeypad((k) => !k)}>
                Teclado
              </Button>
            ) : null}
            {p.onTransfer ? (
              <Button size="sm" variant="secondary" disabled={!connected || p.transfer?.state === 'ringing'} leadingIcon={<Icon icon={ArrowDataTransferHorizontalIcon} aria-hidden />} onClick={p.onTransfer}>
                Transferir
              </Button>
            ) : null}
          </div>
          {keypad && p.onDigit && connected ? <Dialpad mode="dtmf" onDigit={p.onDigit} /> : null}
          <Button variant="danger" leadingIcon={<Icon icon={CallEnd01Icon} aria-hidden />} loading={p.hangingUp} onClick={p.onHangup}>
            Colgar
          </Button>
        </>
      ) : null}
    </section>
  );
}

// ───────────── Transfer ─────────────

export interface TransferTargetOption {
  id: string;
  name: string;
  presence: SoftphonePresence;
}

export type TransferChoice = { toMembershipId: string } | { toNumber: string };

export interface TransferCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targets: readonly TransferTargetOption[];
  onTransfer: (choice: TransferChoice) => void;
  busy?: boolean;
  error?: string | null;
}

/** Choose another advisor (with their presence) or an external number to transfer the current call to. */
export function TransferCallDialog({ open, onOpenChange, targets, onTransfer, busy, error }: TransferCallDialogProps) {
  const [mode, setMode] = React.useState<'advisor' | 'number'>(targets.length ? 'advisor' : 'number');
  const [target, setTarget] = React.useState<string>('');
  const [number, setNumber] = React.useState('');
  React.useEffect(() => {
    if (!open) {
      setTarget('');
      setNumber('');
    }
  }, [open]);
  const valid = mode === 'advisor' ? !!target : number.replace(/[^0-9]/g, '').length >= 3;
  const submit = () => {
    if (!valid) return;
    onTransfer(mode === 'advisor' ? { toMembershipId: target } : { toNumber: number.trim() });
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      dismissible={!busy}
      title="Transferir llamada"
      description="LIA llama al destino y, si contesta, le pasa la llamada."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button leadingIcon={<Icon icon={ArrowDataTransferHorizontalIcon} aria-hidden />} disabled={!valid} loading={busy} onClick={submit}>
            Transferir
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <RadioGroup
          aria-label="Transferir a"
          value={mode}
          onValueChange={(v) => setMode(v as 'advisor' | 'number')}
          options={[
            { value: 'advisor', label: 'Otro asesor', disabled: targets.length === 0, description: targets.length === 0 ? 'No hay otros asesores con softphone en esta conexión.' : undefined },
            { value: 'number', label: 'Un número' },
          ]}
        />
        {mode === 'advisor' && targets.length > 0 ? (
          <FormField label="Asesor">
            <Select
              value={target || undefined}
              onValueChange={setTarget}
              placeholder="Elige un asesor"
              options={targets.map((t) => ({ value: t.id, label: `${t.name} · ${SOFTPHONE_PRESENCE_LABELS[t.presence]}`, disabled: t.presence === 'offline' }))}
            />
          </FormField>
        ) : null}
        {mode === 'number' ? (
          <FormField label="Número de destino" description="E.164 (+52…) o a 10 dígitos.">
            <Input inputMode="tel" autoComplete="off" value={number} maxLength={32} onChange={(e) => setNumber(e.target.value)} />
          </FormField>
        ) : null}
        {error ? (
          <InlineNotice tone="danger" role="alert">
            {error}
          </InlineNotice>
        ) : null}
      </div>
    </Dialog>
  );
}

// ───────────── Floating dock ─────────────

export interface SoftphoneDockProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Short status next to the launcher (e.g. the presence badge or the call state). */
  status?: React.ReactNode;
  /** Highlight the launcher (incoming call). */
  attention?: boolean;
  title?: string;
  children: React.ReactNode;
  className?: string;
}

/** Floating softphone in the bottom corner: a launcher button and a panel that expands above it. */
export function SoftphoneDock({ open, onOpenChange, status, attention, title = 'Softphone', children, className }: SoftphoneDockProps) {
  const panelId = React.useId();
  return (
    <div className={cn('fixed bottom-4 right-4 z-40 grid justify-items-end gap-2', className)}>
      {open ? (
        <section
          id={panelId}
          aria-label={title}
          className="grid max-h-[calc(100dvh-96px)] w-[min(360px,calc(100vw-32px))] grid-rows-[auto_1fr] overflow-hidden rounded-card border border-border-subtle bg-surface-default shadow-overlay animate-fade-in"
        >
          <header className="flex items-center justify-between gap-2 border-b border-border-subtle px-4 py-2">
            <h2 className="text-body-lg font-semibold text-text-primary">{title}</h2>
            <IconButton size="sm" label="Minimizar softphone" icon={<Icon icon={Cancel01Icon} aria-hidden />} onClick={() => onOpenChange(false)} />
          </header>
          <div className="grid content-start gap-4 overflow-y-auto p-4">{children}</div>
        </section>
      ) : null}
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => onOpenChange(!open)}
        className={cn(
          'flex items-center gap-2 rounded-pill border bg-surface-default px-3 py-2 text-body font-medium text-text-primary shadow-overlay',
          'hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
          attention ? 'border-warning' : 'border-border-subtle',
        )}
      >
        <Icon icon={TelephoneIcon} aria-hidden className={cn('size-[18px]', attention ? 'animate-pulse text-warning' : 'text-text-secondary')} />
        <span>{title}</span>
        {status}
      </button>
    </div>
  );
}
